import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Award, CheckCircle, HeartHandshake, MapPin, Mail, ShieldCheck } from 'lucide-react';
import SEO from '../components/SEO';
import useIsMobile from '../hooks/useIsMobile';

const values = [
  { icon: Award, title: 'Experience', body: 'Years of expertise in healthcare equipment installation and facility optimization across the country.' },
  { icon: CheckCircle, title: 'Quality', body: 'Professional installation with attention to detail and an unwavering commitment to excellence.' },
  { icon: ShieldCheck, title: 'Compliance', body: 'Every installation meets ADA accessibility requirements and Joint Commission (JCO) standards — so your facility is inspection-ready from day one.' },
  { icon: HeartHandshake, title: 'Support', body: 'Comprehensive training and ongoing maintenance to ensure long-term success for your facility.' },
];

type StaffMember = {
  id: number;
  name: string;
  title: string;
  location?: string;
  email: string;
  photo: string;
  bio: string;
  specialties: string[];
};

const staff: StaffMember[] = [
  {
    id: 1,
    name: 'Joe Penwell',
    title: 'Project Manager | Co-Owner',
    // location: 'Nationwide',
    email: 'Jpenwell@jvphealthtechsolutions.com',
    photo: '/images/staff/joe.jpg',
    bio: 'Joe brings extensive experience in healthcare technology solutions, project management, and professional equipment installation.  He leads nationwide installation projects, oversees service and sales support, and ensures every client receives reliable, compliant, and high-quality hardware integration. Joe\'s experience and hands-on leadership in the field make him a trusted partner for hospitals, universities, and clinical facilities across the country.',
    specialties: ['Owner', 'Project Management', 'Equipment Installation', 'Client Relations'],
  },
  {
    id: 2,
    name: 'Valerie Penwell',
    title: 'Director of Operations | Co-Owner',
    // location: 'Nationwide',
    email: 'Valerie@jvphealthtechsolutions.com',
    photo: '/images/staff/valerie.jpg',
    bio: 'With 11 years of nursing experience and a strong background in clinical workflow optimization, Valerie oversees business operations and ensures smooth, efficient processes across all JVP projects. Her healthcare expertise allows her to bridge clinical needs with operational strategy, supporting compliance, team coordination, and high-quality service delivery for facilities nationwide.',
    specialties: ['Operations Management', 'Business Process Management', 'Team Coordination'],
  },
];

const AboutPage: React.FC = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const isMobile = useIsMobile();

  return (
    <>
      <SEO
        title="About Us | JVP Health Tech Solutions"
        description="Nationwide healthcare equipment installation company. Learn about our mission, values, ADA & JCO compliance, and the team behind every install."
        keywords="about JVP Health Tech Solutions, healthcare installation company, ADA compliant installs, JCO compliant healthcare, nationwide medical equipment installation team"
        url="https://www.jvphealthtechsolutions.com/about"
      />
      <div className="page" style={{ paddingTop: '80px' }}>

        {/* Hero */}
        <div style={{ background: 'linear-gradient(135deg, var(--color-navy) 0%, var(--color-primary) 100%)', color: 'white', padding: isMobile ? '3rem 1.25rem' : '5rem 2rem', textAlign: 'center' }}>
          <h1 style={{ color: 'white', fontSize: isMobile ? '2rem' : '2.8rem', marginBottom: '1rem' }}>About Us</h1>
          <p style={{ fontSize: '1.2rem', maxWidth: '600px', margin: '0 auto', opacity: 0.9, lineHeight: 1.7 }}>
            Professional healthcare equipment installation — built on expertise, trust, and a commitment to patient care.
          </p>
        </div>

        {/* Mission */}
        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: isMobile ? '3rem 1.25rem' : '5rem 2rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '2rem' : '4rem', alignItems: 'center' }}>
            <div>
              <p style={{ color: 'var(--color-primary)', fontWeight: 600, fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.75rem' }}>Who We Are</p>
              <h2 style={{ color: 'var(--color-text)', fontSize: '2rem', fontWeight: 700, lineHeight: 1.3, marginBottom: '1.25rem' }}>JVP Health Tech Solutions</h2>
              <p style={{ fontSize: '1.05rem', color: 'var(--color-text-secondary)', lineHeight: 1.8, marginBottom: '1rem' }}>
                We provide professional healthcare equipment installation and support services for facilities nationwide.
                Our experienced team specializes in installing medical arms, carts, desks, and complete room configurations
                with precision and expertise.
              </p>
              <p style={{ fontSize: '1.05rem', color: 'var(--color-text-secondary)', lineHeight: 1.8 }}>
                From initial site assessment through final training, we partner with healthcare facilities to ensure
                every installation meets the highest standards of safety, functionality, and care — including ADA
                accessibility requirements and Joint Commission (JCO) compliance.
              </p>
            </div>
            <div style={{ display: 'grid', gap: '1.25rem' }}>
              {values.map(({ icon: Icon, title, body }) => (
                <div key={title} style={{ display: 'flex', gap: '1.25rem', alignItems: 'flex-start', background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid var(--color-surface-alt)' }}>
                  <div style={{ background: 'var(--color-primary-tint)', borderRadius: '10px', padding: '0.75rem', flexShrink: 0 }}>
                    <Icon size={22} color="var(--color-primary)" strokeWidth={2} />
                  </div>
                  <div>
                    <h3 style={{ color: 'var(--color-text)', fontSize: '1rem', fontWeight: 700, marginBottom: '0.3rem' }}>{title}</h3>
                    <p style={{ color: 'var(--color-text-muted)', lineHeight: 1.6, margin: 0, fontSize: '0.95rem' }}>{body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Team */}
        <div style={{ background: 'var(--color-surface)', padding: isMobile ? '3rem 1.25rem' : '5rem 2rem' }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <p style={{ color: 'var(--color-primary)', fontWeight: 600, fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.75rem' }}>The People Behind the Work</p>
            <h2 style={{ color: 'var(--color-text)', fontSize: '2rem', fontWeight: 700, marginBottom: '0.5rem' }}>Meet Our Team</h2>
            <p style={{ color: 'var(--color-text-muted)', marginBottom: '3rem', fontSize: '1.05rem' }}>Dedicated professionals committed to exceptional healthcare equipment installation.</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 320px))', gap: '2rem', justifyContent: 'center' }}>
              {staff.map(member => (
                <div key={member.id} style={{ background: 'white', borderRadius: '14px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.07)', border: '1px solid var(--color-surface-alt)', display: 'flex', flexDirection: 'column' }}>
                  <picture>
                    <source srcSet={member.photo.replace('.jpg', '.avif')} type="image/avif" />
                    <img src={member.photo} alt={member.name} style={{ width: '100%', aspectRatio: '4/5', objectFit: 'cover', objectPosition: 'top' }} />
                  </picture>
                  <div style={{ padding: '2rem', display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                    <h3 style={{ color: 'var(--color-text)', fontSize: '1.2rem', fontWeight: 700, marginBottom: '0.25rem' }}>{member.name}</h3>
                    <p style={{ color: 'var(--color-primary)', fontWeight: 600, fontSize: '0.9rem', marginBottom: '1.25rem' }}>{member.title}</p>
                    <p style={{ color: 'var(--color-text-secondary)', lineHeight: 1.7, fontSize: '0.95rem', marginBottom: '1.5rem' }}>{member.bio}</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1.5rem' }}>
                      {member.specialties.map(s => (
                        <span key={s} style={{ background: 'var(--color-primary-tint)', color: 'var(--color-primary)', fontSize: '0.8rem', fontWeight: 600, padding: '0.3rem 0.75rem', borderRadius: '50px' }}>{s}</span>
                      ))}
                    </div>
                    <div style={{ borderTop: '1px solid var(--color-surface-alt)', paddingTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: 'auto' }}>
                      <a href={`mailto:${member.email}`} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--color-text-secondary)', textDecoration: 'none', fontSize: '0.9rem' }}>
                        <Mail size={15} color="var(--color-primary)" /> {member.email}
                      </a>
                      {member.location && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--color-text-secondary)', fontSize: '0.9rem' }}>
                          <MapPin size={15} color="var(--color-primary)" /> {member.location}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div style={{ background: 'linear-gradient(135deg, var(--color-navy) 0%, var(--color-primary) 100%)', color: 'white', padding: '5rem 2rem', textAlign: 'center' }}>
          <h2 style={{ color: 'white', fontSize: '2.2rem', marginBottom: '1rem' }}>Ready to Work With Us?</h2>
          <p style={{ opacity: 0.9, fontSize: '1.1rem', maxWidth: '500px', margin: '0 auto 2rem' }}>
            Let's talk about your facility's needs and how we can help.
          </p>
          <Link to="/contact" style={{ background: 'white', color: 'var(--color-primary)', padding: '1rem 2.5rem', borderRadius: '50px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem', display: 'inline-block' }}>
            Get in Touch
          </Link>
        </div>

      </div>
    </>
  );
};

export default AboutPage;
